import React, {useEffect, useState} from "react";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import DatePicker, {registerLocale} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {useTranslation} from "react-i18next";
import da from "date-fns/locale/da";

registerLocale("da", da);

const LoadingIcon = ({text, color, strokeWidth}) => {
    return (
        <>
            <div className="spinner">
                <svg
                    viewBox="0 0 24 24"
                    xmlns="<http://www.w3.org/2000/svg>"
                >
                    <circle
                        cx="12" cy="12" r="8"
                        strokeWidth={strokeWidth} stroke={color}
                        fill="none"
                    />
                </svg>
            </div>
            <div>{text}</div>
        </>
    )
}

const isValidEmail = email =>
// eslint-disable-next-line no-useless-escape
/^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
    email
);

const validateDateString = dateString => {
    var regEx = /^\d{4}-\d{2}-\d{2}$/;
    if(!dateString.match(regEx)) return false;  // Invalid format
    var d = new Date(dateString);
    var dNum = d.getTime();
    if(!dNum && dNum !== 0) return false; // NaN value, Invalid date
    if (d.getFullYear() < 1900) return false;
    if (d.getFullYear() > new Date().getFullYear()) return false;
    return d.toISOString().slice(0,10) === dateString;
};

const isValidDate = (dateStr, language) => {
    switch (language) {
        case 'sv':
            // allow yyyy-mm-dd | yyyymmdd
            const svRegex = /^\d{4}-\d{2}-\d{2}|\d{4}\d{2}\d{2}$/;

            if (dateStr.match(svRegex) === null) {
                return false;
            }

            if (dateStr.includes('-') === false) {
                const {year, month, day} = dateStr.match(/(?<year>\d{4})(?<month>\d{2})(?<day>\d{2})/, 'ig').groups;
                dateStr = `${year}-${month}-${day}`;
            }

            break;
        default:
            // allow dd/mm/yyyy | ddmmyyyy
            const daRegex = /^\d{2}\/\d{2}\/\d{4}|\d{2}\d{2}\d{4}$/;

            if (dateStr.match(daRegex) === null) {
                return false;
            }

            if (dateStr.includes('/') === false) {
                const {day, month, year} = dateStr.match(/(?<year>\d{2})(?<month>\d{2})(?<day>\d{4})/, 'ig').groups;
                dateStr = `${year}/${month}/${day}`;
            }

            const [day,month,year] = dateStr.split('/');
            dateStr = `${year}-${month}-${day}`;

            break;
    }

    return validateDateString(dateStr);
    /*
    if (validateDateString(dateStr)) {
        const date = new Date(dateStr);
        const timestamp = date.getTime();

        if (typeof timestamp !== 'number' || Number.isNaN(timestamp)) {
            return false;
        }

        let c = new Date();
        if (timestamp > c.getTime()) {
            return false;
        }

        return date.getFullYear() >= 1900;
    }

    return false;
     */
};

const isValidPhonenumber = phonenumber => {

    const regex = /^\+?[1-9][0-9]{7,10}$/;

    if (phonenumber.match(regex) === null) {
        return true;
    }

    const consecutive = /(?!.*([1-9]).*\1)^[1-9]{7,10}/
    if (phonenumber.match(consecutive)) {
        return false;
    }

    const duplicateNumbers = /(.)\1{7,10}/gm;
    return !phonenumber.match(duplicateNumbers);
};


function App() {
    const [submitted, setSubmitted] = useState(false);
    const maxNumberOfTravellers = 9;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [accommodationOptions, setAccommodationOptions] = useState([]);
    const [excludedates, setExcludedates] = useState([]);
    const [durations, setDurations] = useState([]);
    const [journeys, setJourneys] = useState([]);
    const [currentJourney, setCurrentJourney] = useState('-1');
    const [departureLocations, setDepartureLocations] = useState([]);
    const [sending, setSending] = useState(false);
    const {t} = useTranslation('common');
    const {register, formState: {errors}, handleSubmit, control, reset, resetField} = useForm({
        defaultValues: {
            travellers: [
                {name: "", birthday: ""},
                {name: "", birthday: ""}
            ]
        }
    });

    const origin = document.getElementById('root').dataset.origin;
    const language = document.getElementById('root').dataset.language;

    useEffect(() => {
        let formData = new FormData();
        formData.append('caller', 'request-for-quote');
        formData.append('origin', origin);
        formData.append('language', language);

        fetch(process.env.REACT_APP_API_DOMAIN + '/?type=73495', {
            method: 'POST',
            body: formData
        })
            .then((response) => {
                if (!response.ok) {
                    throw new Error(
                        'This is an HTTP error: The status is ${response.status}'
                    );
                }
                return response.json();
            })
            .then((actualData) => {
                setJourneys(actualData?.journeys);
                let cJ = parseInt(document.getElementById('root').dataset.journeyuid);
                if (!isNaN(cJ)) {
                    setCurrentJourney(cJ);
                    if (actualData?.journeys.length) {
                        actualData?.journeys.map((option) => {
                            if (parseInt(option.uid) === cJ) {
                                if (option?.accomodation.length) {
                                    setAccommodationOptions(option.accomodation);
                                }

                                if (option?.excludedates) {
                                    setExcludedates(option.excludedates);
                                }

                                if (option?.airports) {
                                    setDepartureLocations(option.airports);
                                }

                                if (option?.durations) {
                                    setDurations(option.durations);
                                }
                            }
                        });
                    }
                }
                setError(null);
            })
            .catch((err) => {
                setError(err.message);
            })
            .finally(() => {
                setLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = (data) => {
        setSending(true);
        let formData = new FormData();

        let date = new Date(data.departuredate);
        data.departuredate = Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
        formData.append('caller', 'request-for-quote-submitted');
        formData.append('data', JSON.stringify(data));

        fetch(process.env.REACT_APP_API_DOMAIN + '/?type=73495', {
            method: 'POST',
            body: formData
        }).then((response) => {
            if (!response.ok) {
                throw new Error(
                    'This is an HTTP error: The status is ${response.status}'
                );
            }
            return response.json();
        })
            .then((actualData) => {
                setSubmitted(true);
                reset();
            })
            .catch((err) => {
            })
            .finally(() => {
                setSending(false);
            });
    };

    const isAllowedDay = (date) => {
        const day = date.getDay();

        if (excludedates.includes((date.getTime() / 1000))) {
            return false;
        }

        if (origin === 'nyati') {
            return (day === 5) && date.getTime() > Date.now();
        }

        return date.getTime() > Date.now();
    };

    const handleEmailValidation = email => {
        return isValidEmail(email);
    };

    const handleDateValidation = date => {
        return isValidDate(date, language);
    }

    const handlePhonenumberValidation = phonenumber => {
        return isValidPhonenumber(phonenumber);
    }

    const {
        fields,
        append,
        remove
    } = useFieldArray({
        rules: {minLength: 1},
        control,
        name: "travellers"
    });

    const hideForm = (event) => {
        //setSubmitted(false);
        document.getElementById('root').classList.add("hidden");

        const customEvent = document.createEvent("Event");
        customEvent.initEvent("closeReactForm", true, true);
        event.target.dispatchEvent(customEvent);
    }

    const onChangeJourney = (event) => {
        setFieldDataByJourneyUid(event.target.value);
        resetField('accommodation');
        resetField('departuredate');

    }

    function setFieldDataByJourneyUid(uid) {
        journeys.map((option) => {
           if (parseInt(option.uid) === parseInt(uid)) {
               setAccommodationOptions(option.accomodation);
               setExcludedates(option.excludedates);

               resetField('departureLocation');
               setDepartureLocations(option.airports);

               resetField('days');
               setDurations(option.durations);
           }
        });
    }

    const form = () => {
        return (
            <form onSubmit={handleSubmit(onSubmit)} className="fade-in">
                <div className="formtitle specialfont01">{t('form.formtitle')}</div>
                <div className="elementWrap">
                    <label>{t('form.travelpack')}</label>
                    <select name="journey" {...register("journey", {required: true})} defaultValue={currentJourney} onChange={onChangeJourney}>
                        <option value="-1">{t('form.please-select')}</option>

                        {journeys.map((option) => (
                            option?.accomodation.length > 0
                            ?  (<option key={option.uid} value={option.uid}>{option.title}</option>)
                                : ''
                           ))}

                    </select>
                    {errors?.travelpack?.type === 'required' && <p className="alert">{t('form.errors.required')}</p>}
                </div>

                <div className="elementWrap">
                    <label>{t('form.departureLocation')}</label>
                    <select name="departureLocation" {...register("departureLocation", {required: true})}>
                        <option value="">{t('form.please-select')}</option>

                        {departureLocations.map((option) => (
                            <option key={option} value={option}>{option}</option>)
                        )}

                    </select>
                    {errors?.departureLocation?.type === 'required' && <p className="alert">{t('form.errors.required')}</p>}
                </div>

                <div className="elementWrap">
                    <label>{t('form.desired-departure-date')}</label>
                    <Controller
                        control={control}
                        name="departuredate"
                        rules={{required: true}}
                        render={({field}) => (
                            <DatePicker
                                locale={da}
                                dateFormat="dd/MM/yyyy"
                                placeholderText={t('form.pick-date')}
                                onChange={(date) => field.onChange(date)} filterDate={isAllowedDay}
                                showWeekNumbers
                                selected={field.value}
                                onFocus={(e) => e.target.setAttribute('inputMode', 'none')}
                            />
                        )}
                    />
                    {errors?.departuredate && <p className="alert">{t('form.errors.required')}</p>}
                </div>

                <div className="elementWrap">
                    <label>{origin === 'nyati' ? t('form.number-of-days-nyati') : t('form.number-of-days-milima')}</label>
                    <select name="days" {...register("days", {required: true})}>
                        <option value="">{t('form.please-select')}</option>
                        {durations.map((option) => (
                            <option key={option} value={option}>{option} {t('form.days')}</option>)
                        )}
                    </select>
                    {errors?.days?.type === 'required' && <p className="alert">{t('form.errors.required')}</p>}
                </div>

                <section>
                    <div className="elementWrap">
                        <div className="travellers">
                            {fields.map((item, index) => {
                                return (
                                    <div className="details" key={item.id}>
                                        <label>{index + 1}. {t('form.traveller-name')}</label>
                                        <input
                                            type="text"
                                            placeholder={t('form.traveller') + ' ' + (index + 1)} {...register(`travellers.${index}.name`, {required: true})}
                                        />

                                        <label>{t('form.traveller-birthday')}</label>
                                        <input
                                            type="text" inputMode="numeric"
                                            placeholder={t('form.traveller-birthday-placeholder')} {...register(`travellers.${index}.birthday`, {
                                                required: true,
                                                validate: handleDateValidation
                                            })}
                                        />

                                        {index > 0 ? (
                                            <button type="button" className="btn-remove-traveller specialfont01"
                                                    onClick={() => remove(index)}>
                                                {t('form.btn-delete-traveller')}
                                            </button>) : ''
                                        }

                                        {errors.travellers && errors.travellers[index] &&
                                        <p className="alert">{t('form.errors.traveller.required')}</p>}
                                    </div>
                                );
                            })}
                        </div>

                        {fields.length < maxNumberOfTravellers ? (
                            <button type="button" className="btn-add-traveller green specialfont01" onClick={() => {
                                append({name: "", birthday: ""});
                            }}>{t('form.btn-add')} {fields.length + 1}. {t('form.btn-add-traveller')}
                            </button>) : (<div className="notice">{t('form.traveller-max-text')}</div>)}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.desired-accomodation')}</label>
                        <select name="accommodation" {...register("accommodation", {required: true})}>
                            <option value="">{t('form.please-select')}</option>

                            {accommodationOptions.map((option, index) => (
                                <option key={index} value={option}>{option}</option>)
                            )}

                        </select>
                        {errors?.accommodation?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.comments')}</label>
                        <textarea name="comments" maxLength="300" {...register("comments")}></textarea>
                    </div>
                </section>

                <section>
                    <div className="elementWrap">
                        <label>{t('form.contactperson.name')}</label>
                        <input
                            type="text"
                            {...register("contactname", {required: true, maxLength: 255})}
                        />
                        {errors?.contactname?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.contactperson.email')}</label>
                        <input
                            type="text" inputMode="email"
                            {...register("contactemail", {
                                required: true,
                                validate: handleEmailValidation
                            })}
                        />
                        {errors?.contactemail?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                        {errors?.contactemail?.type === 'validate' &&
                        <p className="alert">{t('form.errors.invalid-email')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.contactperson.telephone')}</label>
                        <input
                            type="tel" inputMode="tel" maxLength="10"
                            {...register("contactphone", {
                                required: true,
                                validate: handlePhonenumberValidation
                            })}
                        />
                        {errors?.contactphone?.type === 'required' || errors?.contactphone?.type === 'validate' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.contactperson.address')}</label>
                        <input
                            type="text"
                            {...register("contactaddress", {required: true, maxLength: 255})}
                        />
                        {errors?.contactaddress?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.contactperson.zipcode')}</label>
                        <input
                            type="text" inputMode="tel"
                            {...register("contactzipcode", {required: true, maxLength: 20})}
                        />
                        {errors?.contactzipcode?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>

                    <div className="elementWrap">
                        <label>{t('form.contactperson.city')}</label>
                        <input
                            type="text"
                            {...register("contactcity", {required: true, maxLength: 255})}
                        />
                        {errors?.contactcity?.type === 'required' &&
                        <p className="alert">{t('form.errors.required')}</p>}
                    </div>
                </section>

                <div className="elementWrap">
                    <label className="checkContainer">{t('form.consent')}
                        <input type="checkbox"  {...register("gdpr", {required: true})}/>
                        <span className="checkmark"></span>
                        {errors?.gdpr?.type === 'required' && <p className="alert">{t('form.errors.required')}</p>}
                    </label>
                </div>
                <input type="submit" className="specialfont01" value={t('form.submit')} disabled={sending}/>
            </form>
        )
    }

    const formCompleted = () => {
        return (
            <div className="form-submitted fade-in">
                <div className="formtitle specialfont01">{t('form.submitted.title')}</div>
                <p>{t('form.submitted.text')}<br/>{t('form.submitted.spam')}</p>
                <button className="green" onClick={hideForm}>{t('form.submitted.close')}</button>
            </div>
        )
    }

    if (loading) return <div className="loading"><LoadingIcon text={t('form.wait')} color="#E9E4D1" strokeWidth="2"/></div>;

    return (
        <>
            <svg onClick={hideForm} className="close" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M437.5,386.6L306.9,256l130.6-130.6c14.1-14.1,14.1-36.8,0-50.9c-14.1-14.1-36.8-14.1-50.9,0L256,205.1L125.4,74.5  c-14.1-14.1-36.8-14.1-50.9,0c-14.1,14.1-14.1,36.8,0,50.9L205.1,256L74.5,386.6c-14.1,14.1-14.1,36.8,0,50.9  c14.1,14.1,36.8,14.1,50.9,0L256,306.9l130.6,130.6c14.1,14.1,36.8,14.1,50.9,0C451.5,423.4,451.5,400.6,437.5,386.6z"/>
            </svg>
            <div id="contact-form">
                {submitted ? (formCompleted()) : (form())}
            </div>
        </>
    );
}

export default App;